import React, { useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  Button,
  useTheme,
  Modal,
  Box,
  CircularProgress,
  IconButton,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';

const FlightItineraryPDFButton = () => {
  const theme = useTheme();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const [canvasData, setCanvasData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGeneratePreview = async () => {
    setLoading(true);

    const element = document.querySelector('#flight-itinerary-pdf');
    if (!element) return;

    const originalLetterSpacing = element.style.letterSpacing;
    element.style.letterSpacing = '0.06em';

    await document.fonts.ready;

    const canvas = await html2canvas(element, {
      scale: window.devicePixelRatio * 1,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: true,
    });

    // Convertir a gris
    const grayscaleCanvas = document.createElement('canvas');
    grayscaleCanvas.width = canvas.width;
    grayscaleCanvas.height = canvas.height;
    const ctx = grayscaleCanvas.getContext('2d');
    ctx.drawImage(canvas, 0, 0);

    const imageData = ctx.getImageData(
      0,
      0,
      grayscaleCanvas.width,
      grayscaleCanvas.height
    );
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const gray = 0.3 * r + 0.59 * g + 0.11 * b;
      data[i] = data[i + 1] = data[i + 2] = gray;
    }
    ctx.putImageData(imageData, 0, 0);

    const imgData = grayscaleCanvas.toDataURL('image/png');

    setCanvasData(grayscaleCanvas);
    setPreviewImg(imgData);
    setPreviewOpen(true);

    element.style.letterSpacing = originalLetterSpacing;
    setLoading(false); // Ocultar spinner
  };

  const handleDownload = () => {
    if (!canvasData) return;

    const imgData = canvasData.toDataURL('image/png');
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const usableWidth = pdfWidth - margin * 2;
    const usableHeight = pdfHeight - margin * 2;

    const imgWidth = usableWidth;
    const imgHeight = (canvasData.height * imgWidth) / canvasData.width;

    let finalWidth = imgWidth;
    let finalHeight = imgHeight;
    if (imgHeight > usableHeight) {
      const scale = usableHeight / imgHeight;
      finalHeight = usableHeight;
      finalWidth = imgWidth * scale;
    }

    const xOffset = (pdfWidth - finalWidth) / 2;
    const yOffset = margin;

    pdf.addImage(imgData, 'PNG', xOffset, yOffset, finalWidth, finalHeight);
    pdf.save('itinerario-vuelo.pdf');
    setPreviewOpen(false);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewImg(null);
    setCanvasData(null);
  };

  const handleCloseModal = () => {
    if (loading) return; // bloquea cierre mientras carga
    handleClosePreview();
  };

  return (
    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
      <Button
        variant="outlined"
        startIcon={<PictureAsPdfIcon />}
        onClick={handleGeneratePreview}
        sx={{
          width: { xs: '70%', md: '50%' },
          borderRadius: '8px',
          fontWeight: 600,
          fontSize: '1rem',
          borderColor: '#000',
          color: '#000',
          '&:hover': {
            color: theme.text1,
            borderColor: theme.primary,
            bgcolor: theme.primary,
          },
        }}
      >
        Descargar Itinerario
      </Button>

      <Modal open={loading || previewOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1300,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              bgcolor: 'background.paper',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: loading ? 'center' : 'flex-start',
              alignItems: 'center',
              width: { xs: '95%', sm: '70%', md: '60%' },
              height: { xs: '95%', sm: '80%', md: '80%' },
              overflow: 'auto', // permite scrollear si PDF muy largo
            }}
          >
            {/* Cruz de cerrar solo si no está cargando */}
            {!loading && (
              <IconButton
                onClick={handleClosePreview}
                sx={{ position: 'absolute', top: 8, right: 8 }}
              >
                <CloseIcon />
              </IconButton>
            )}

            {loading && (
              <>
                <CircularProgress />
                <Box sx={{ mt: 2 }}>Generando PDF...</Box>
              </>
            )}

            {previewOpen && previewImg && !loading && (
              <>
                <img
                  src={previewImg}
                  alt="Vista previa del PDF"
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '95vh',
                    objectFit: 'contain',
                  }}
                />

                <Button
                  variant="contained"
                  onClick={handleDownload}
                  sx={{
                    mt: 2,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: theme.primary,
                      color: theme.text1,
                    },
                  }}
                >
                  Descargar PDF
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default FlightItineraryPDFButton;
